import DomainModel from "../domain-model";
import logging from '../logging';
import removeElement from "../mem/remove-element";
import { ClosetUnit, Cubby, Door, Drawer, GenericAsset, HookRack, Murphybed, ParametricPart, Section, VerticalPanel, Wall } from "../domain";
import { removed } from "../utils";
import { getAdjacentDoors } from "./door-controllers";
import { getAdjacentCubbies } from "./cubby-controllers";

const log = logging('deleteModel');

export const canDeleteModel = (model: DomainModel): boolean => {
    if(model instanceof ClosetUnit) {
        return false;
    }
    if(model instanceof Section && model.parent instanceof ClosetUnit) {
        return false;
    }
    return true;
}

/**
 * Attempts to delete the given item or logs an error message if is something
 * that cannot be deleted. You can check if the thing can be deleted by calling
 * canDeleteModel() before calling this method.
 * @returns a string name for the type of model, e.g. Section, Cubby, etc.
 */
export const deleteModel = (model: DomainModel): string => {
    if(model instanceof Cubby) {
        deleteCubby(model);
        return 'Cubby';
    }
    if(model instanceof Door) {
        deleteDoor(model);
        return 'Door';
    }
    if(model instanceof Drawer) {
        deleteDrawer(model);
        return 'Drawer';
    }
    if(model instanceof GenericAsset) {
        deleteGenericAsset(model);
        return 'Generic Asset';
    }
    if(model instanceof HookRack) {
        deleteHookRack(model);
        return 'Hook Rack';
    }
    if(model instanceof Murphybed) {
        deleteMurphybed(model);
        return 'Murphy Bed';
    }
    if(model instanceof ParametricPart) {
        deleteParametricPart(model);
        return model.name;
    }
    if(model instanceof VerticalPanel) {
        deleteVerticalPanel(model);
        return 'Panel';
    }

    log.error('Unable to delete unknown model type', model);
    return 'ERROR';
}

export const deleteCubby = (cubby: Cubby) => {
    if(cubby.parent instanceof Section) {
        removeElement(cubby.parent.cubbies, cubby);
    }
    if(cubby.parent instanceof Wall) {
        cubby.parent.cubbies = removed(cubby, cubby.parent.cubbies);
    }
}

export const deleteDoor = (door: Door) => {
    if(door.parent instanceof Section) {
        removeElement(door.parent.doors, door);
    }
    else if(door.parent instanceof Wall) {
        door.parent.doors = removed(door, door.parent.doors);
    }
}

export const deleteDrawer = (drawer: Drawer) => {
    if(drawer.parent instanceof Section || drawer.parent instanceof Wall) {
        drawer.parent.drawers = removed(drawer, drawer.parent.drawers);
    }
}

export const deleteGenericAsset = (model: GenericAsset) => {
    if(model.parent instanceof Section || model.parent instanceof Wall) {
        model.parent.assets = removed(model, model.parent.assets);
    }
}

export const deleteHookRack = (rack: HookRack) => {
    if(rack.parent instanceof Wall) {
        rack.parent.hookRacks = removed(rack, rack.parent.hookRacks);
    }
}

export const deleteMurphybed = (bed: Murphybed) => {
    if(bed.parent instanceof Wall) {
        bed.parent.beds = removed(bed, bed.parent.beds);
    }
}

export const deleteParametricPart = (part: ParametricPart) => {
    if(part.parent instanceof Section) {
        part.parent.parametricParts = removed(part, part.parent.parametricParts);

        if(isFixedShelf(part)) {
            const
                section = part.parent,
                [doorA, doorB] = getAdjacentDoors(section, part),
                [cubbyA, cubbyB] = getAdjacentCubbies(section, part);
            if(doorA) deleteDoor(doorA);
            if(doorB) deleteDoor(doorB);
            if(cubbyA) deleteCubby(cubbyA);
            if(cubbyB) deleteCubby(cubbyB);
        }
    }
}

export const deleteVerticalPanel = (panel: VerticalPanel) => {
    if(panel.parent instanceof Wall) {
        panel.parent.panels = removed(panel, panel.parent.panels);
    }
}

const isFixedShelf = (part: ParametricPart): boolean => {
    return part.name?.includes('Fixed');
}
